// How a turn that did not run to its end reads in the chat.
//
// Closing the app while a turn was running — «закрыть MultiTool, пока он
// думает» — used to come back after relaunch as «Модель вернула пустой ответ»:
// the dead process never recorded why the message ended, the relaunched sidecar
// reported the session idle, and an idle turn without an answer read as the
// model's fault. The sidecar now records the reason (session/interrupted.ts);
// these are the two decisions the turn makes from it.
//
// Kept in its own module (like session-turn-key-refused.ts) so they are
// testable without importing a Solid component.

import type { AssistantMessage } from "@opencode-ai/sdk/v2/client"

// The abort reason the sidecar records when the app stopped under a turn —
// quit mid-answer, or killed and closed out on the next launch
// (packages/opencode/src/session/interrupted.ts, APP_CLOSED). Same exact text.
export const APP_CLOSED_REASON = "The app was closed while this turn was running"

type Step = Pick<AssistantMessage, "time" | "error">

/**
 * Cut off by the app closing rather than by Stop. The user never chose to end
 * this answer, so the turn says why it stops where it does and offers Retry.
 */
export function closedByApp(messages: readonly Step[]) {
  return messages.some((m) => m.error?.name === "MessageAbortedError" && m.error.data.message === APP_CLOSED_REASON)
}

/**
 * Every step recorded its end. «Empty answer» is a claim about what the model
 * sent, so it needs this: a step without a completion time was cut off, not
 * answered empty.
 */
export function finished(messages: readonly Step[]) {
  return messages.every((m) => typeof m.time.completed === "number")
}
